import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Terminal,
  RefreshCw,
  Search,
  Plus,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  Settings,
  Filter,
  Key,
  Copy,
  Eye,
  EyeOff,
  FileText,
  CreditCard,
  Percent,
  DollarSign,
  Download,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { terminalsAPI, providersAPI, merchantsAPI } from '../services/api';

const CURRENCIES = [
  { code: 'KZT', name: 'Тенге (₸)', symbol: '₸' },
  { code: 'RUB', name: 'Рубль (₽)', symbol: '₽' },
  { code: 'USD', name: 'Доллар ($)', symbol: '$' },
  { code: 'EUR', name: 'Евро (€)', symbol: '€' },
  { code: 'UZS', name: 'Сум (сўм)', symbol: 'сўм' },
  { code: 'KGS', name: 'Сом (с)', symbol: 'с' },
  { code: 'MDL', name: 'Лей (L)', symbol: 'L' },
  { code: 'AED', name: 'Дирхам (د.إ)', symbol: 'د.إ' },
  { code: 'GBP', name: 'Фунт (£)', symbol: '£' },
  { code: 'CNY', name: 'Юань (¥)', symbol: '¥' },
  { code: 'TRY', name: 'Лира (₺)', symbol: '₺' },
];

const PROVIDERS = [
  { id: "kapitalbank", name: "Kapitalbank UZ", supported_cards: ["visa", "mastercard", "uzcard", "humo"], supported_methods: ["card"] },
  { id: "mrc", name: "MRC (МИР)", supported_cards: ["mir"], supported_methods: ["card"] },
  { id: "mts", name: "MTS Bank", supported_cards: ["mir"], supported_methods: ["card", "sbp"] },
  { id: "mts_sbp", name: "MTS Bank (СБП)", supported_cards: [], supported_methods: ["sbp"] },
  { id: "finzo", name: "Finzo", supported_cards: ["visa", "mastercard"], supported_methods: ["card"] },
  { id: "jusan", name: "Jusan Bank", supported_cards: ["visa", "mastercard"], supported_methods: ["card"] },
  { id: "halyk", name: "Halyk Bank", supported_cards: ["visa", "mastercard"], supported_methods: ["card"] },
  { id: "forte", name: "Forte Bank", supported_cards: ["visa", "mastercard"], supported_methods: ["card"] },
  { id: "sberbank_sbp", name: "Сбербанк (СБП)", supported_cards: [], supported_methods: ["sbp"] },
  { id: "tinkoff_sbp", name: "Тинькофф (СБП)", supported_cards: [], supported_methods: ["sbp"] },
];

const PAYMENT_METHODS = [
  { id: "card", name: "Карта", description: "Оплата банковской картой" },
  { id: "sbp", name: "СБП", description: "Система быстрых платежей" },
];

const TerminalsPage = () => {
  const { hasPermission } = useAuth();
  const { t, language } = useLanguage();
  const [terminals, setTerminals] = useState([]);
  const [providers, setProviders] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [filterProvider, setFilterProvider] = useState('all');
  
  const [showDialog, setShowDialog] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    merchant_id: 'none',
    currency: 'KZT',
    is_active: true,
    // Commission settings
    commission_percent: '',
    commission_min: '',
    commission_max: '',
    // Payment limits
    payment_min: '',
    payment_max: '',
    // Refund/Chargeback commissions
    refund_commission: '',
    chargeback_commission: '',
    // Gateway provider credentials
    gateway_provider: 'mrc',
    gw_merchant_id: '',
    gw_ext_entity_id: '',
    gw_login: '',
    gw_password: '',
    gw_client_id: '',
    gw_account: '',
    gw_secret: '',
    gw_payment_type: 'redirect',
    gw_qrc_type: '02',
    // Finzo credentials
    gw_finzo_public_key: '',
    gw_finzo_secret_key: '',
    gw_finzo_test_mode: true,
  });
  const [saving, setSaving] = useState(false);
  
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletingTerminal, setDeletingTerminal] = useState(null);
  
  // API Keys state
  const [keysDialog, setKeysDialog] = useState(false);
  const [selectedTerminalKeys, setSelectedTerminalKeys] = useState(null);
  const [showSecretKey, setShowSecretKey] = useState(false);

  // Routing dialog state
  const [showRoutingDialog, setShowRoutingDialog] = useState(false);
  const [routingTerminal, setRoutingTerminal] = useState(null);
  const [routingData, setRoutingData] = useState(null);
  const [loadingRouting, setLoadingRouting] = useState(false);
  const [savingRouting, setSavingRouting] = useState(false);
  const [testCard, setTestCard] = useState("");
  const [testResult, setTestResult] = useState(null);

  // Test data dialog state
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testTerminal, setTestTerminal] = useState(null);
  const [testData, setTestData] = useState(null);
  const [loadingTest, setLoadingTest] = useState(false);

  const canWrite = hasPermission('merchants', 'write');

  const fetchTerminals = async (page = 1, searchQuery = search) => {
    try {
      setLoading(true);
      const params = { page, page_size: pagination.pageSize };
      if (searchQuery) params.search = searchQuery;
      if (filterProvider && filterProvider !== 'all') params.provider_id = filterProvider;
      
      const response = await terminalsAPI.list(params);
      setTerminals(response.data.terminals || []);
      setPagination({
        page: response.data.page,
        pageSize: response.data.page_size,
        total: response.data.total,
      });
    } catch (err) {
      toast.error('Ошибка загрузки терминалов');
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await providersAPI.list({ page_size: 100 });
      setProviders(response.data.providers || []);
    } catch (err) {
      console.error('Failed to fetch providers:', err);
    }
  };

  const fetchMerchants = async () => {
    try {
      const response = await merchantsAPI.list({ page_size: 100 });
      setMerchants(response.data.merchants || []);
    } catch (err) {
      console.error('Failed to fetch merchants:', err);
    }
  };

  useEffect(() => {
    fetchTerminals();
    fetchProviders();
    fetchMerchants();
  }, []);

  useEffect(() => {
    fetchTerminals(1, search);
  }, [filterProvider]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTerminals(1, search);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      merchant_id: 'none',
      currency: 'KZT',
      is_active: true,
      commission_percent: '',
      commission_min: '',
      commission_max: '',
      payment_min: '',
      payment_max: '',
      refund_commission: '',
      chargeback_commission: '',
    });
  };

  const openCreateDialog = () => {
    setEditingTerminal(null);
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (terminal) => {
    setEditingTerminal(terminal);
    const settings = terminal.settings || {};
    const commissions = settings.commissions || terminal.commissions || {};
    const limits = settings.limits || terminal.limits || {};
    const gw = settings.gateway_credentials || {};
    
    setFormData({
      name: terminal.name,
      merchant_id: terminal.merchant_id?.toString() || 'none',
      currency: settings.currency || terminal.currency || 'KZT',
      is_active: terminal.is_active,
      commission_percent: commissions.percent || commissions.commission_percent || '',
      commission_min: commissions.min || commissions.commission_min || '',
      commission_max: commissions.max || commissions.commission_max || '',
      payment_min: limits.payment_min || limits.min || '',
      payment_max: limits.payment_max || limits.max || '',
      refund_commission: commissions.refund || commissions.refund_commission || '',
      chargeback_commission: commissions.chargeback || commissions.chargeback_commission || '',
      // Gateway credentials
      gateway_provider: gw.provider || 'mrc',
      gw_merchant_id: gw.merchant_id || '',
      gw_ext_entity_id: gw.ext_entity_id || '',
      gw_login: gw.login || '',
      gw_password: gw.password || '',
      gw_client_id: gw.client_id || '',
      gw_account: gw.account || '',
      gw_secret: gw.secret || '',
      gw_payment_type: gw.payment_type || 'redirect',
      gw_qrc_type: gw.qrc_type || '02',
      // Finzo credentials
      gw_finzo_public_key: gw.public_key || '',
      gw_finzo_secret_key: gw.secret_key || '',
      gw_finzo_test_mode: gw.test_mode !== false,
    });
    setShowDialog(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Введите название терминала');
      return;
    }

    try {
      setSaving(true);
      
      const payload = {
        name: formData.name,
        merchant_id: formData.merchant_id && formData.merchant_id !== 'none' ? parseInt(formData.merchant_id) : null,
        is_active: formData.is_active,
        settings: {
          currency: formData.currency,
          commissions: {
            percent: formData.commission_percent ? parseFloat(formData.commission_percent) : null,
            min: formData.commission_min ? parseFloat(formData.commission_min) : null,
            max: formData.commission_max ? parseFloat(formData.commission_max) : null,
            refund: formData.refund_commission ? parseFloat(formData.refund_commission) : null,
            chargeback: formData.chargeback_commission ? parseFloat(formData.chargeback_commission) : null,
          },
          limits: {
            payment_min: formData.payment_min ? parseFloat(formData.payment_min) : null,
            payment_max: formData.payment_max ? parseFloat(formData.payment_max) : null,
          },
          gateway_credentials: {
            provider: formData.gateway_provider || 'mrc',
            merchant_id: formData.gw_merchant_id || null,
            ext_entity_id: formData.gw_ext_entity_id || null,
            login: formData.gw_login || null,
            password: formData.gw_password || null,
            client_id: formData.gw_client_id || null,
            account: formData.gw_account || null,
            secret: formData.gw_secret || null,
            payment_type: formData.gw_payment_type || 'redirect',
            qrc_type: formData.gw_qrc_type || '02',
            // Finzo credentials
            public_key: formData.gw_finzo_public_key || null,
            secret_key: formData.gw_finzo_secret_key || null,
            test_mode: formData.gw_finzo_test_mode,
          },
        },
      };

      if (editingTerminal) {
        await terminalsAPI.update(editingTerminal.id, payload);
        toast.success('Терминал обновлен');
      } else {
        await terminalsAPI.create(payload);
        toast.success('Терминал создан');
      }
      setShowDialog(false);
      fetchTerminals(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (terminal) => {
    setDeletingTerminal(terminal);
    setDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deletingTerminal) return;
    
    try {
      await terminalsAPI.delete(deletingTerminal.id);
      toast.success('Терминал удален');
      setDeleteDialog(false);
      setDeletingTerminal(null);
      fetchTerminals(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const openKeysDialog = (terminal) => {
    setSelectedTerminalKeys(terminal);
    setShowSecretKey(false);
    setKeysDialog(true);
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} скопирован`);
    } catch (err) {
      toast.error('Ошибка копирования');
    }
  };

  // Routing functions
  const openRoutingDialog = async (terminal) => {
    setRoutingTerminal(terminal);
    setShowRoutingDialog(true);
    setLoadingRouting(true);
    setTestCard("");
    setTestResult(null);
    try {
      const response = await api.get(`/terminals/${terminal.id}/routing`);
      setRoutingData(response.data);
    } catch (err) {
      setRoutingData({
        terminal_id: terminal.id,
        enabled: false,
        default_provider: "kapitalbank",
        default_payment_method: "card",
        routing_rules: [],
      });
    } finally {
      setLoadingRouting(false);
    }
  };

  const saveRouting = async () => {
    if (!routingTerminal || !routingData) return;
    setSavingRouting(true);
    try {
      await api.put(`/terminals/${routingTerminal.id}/routing`, routingData);
      toast.success("Настройки роутинга сохранены");
      fetchTerminals(pagination.page);
    } catch (err) {
      toast.error("Ошибка сохранения");
    } finally {
      setSavingRouting(false);
    }
  };

  const testCardRouting = async () => {
    if (!routingTerminal || !testCard || testCard.length < 6) return;
    try {
      const res = await api.post(`/terminals/${routingTerminal.id}/check-card`, { card_number: testCard });
      setTestResult(res.data);
    } catch (err) {
      toast.error("Ошибка проверки карты");
    }
  };

  const addRoutingRule = () => {
    if (!routingData) return;
    const newRule = { id: Date.now(), card_types: [], provider: "", payment_method: "card", priority: 10, description: "" };
    setRoutingData({ ...routingData, routing_rules: [...(routingData.routing_rules || []), newRule] });
  };

  const updateRule = (ruleId, field, value) => {
    if (!routingData) return;
    setRoutingData({
      ...routingData,
      routing_rules: routingData.routing_rules.map(r => r.id === ruleId ? { ...r, [field]: value } : r)
    });
  };

  const deleteRule = (ruleId) => {
    if (!routingData) return;
    setRoutingData({ ...routingData, routing_rules: routingData.routing_rules.filter(r => r.id !== ruleId) });
  };

  // Test data functions
  const openTestDialog = async (terminal) => {
    setTestTerminal(terminal);
    setShowTestDialog(true);
    setLoadingTest(true);
    try {
      const res = await api.get(`/terminals/${terminal.id}/test-data`);
      setTestData(res.data);
    } catch (err) {
      toast.error("Ошибка загрузки тестовых данных");
      setShowTestDialog(false);
    } finally {
      setLoadingTest(false);
    }
  };

  const toggleTestMode = async (enabled) => {
    if (!testTerminal) return;
    try {
      await api.put(`/terminals/${testTerminal.id}/test-mode`, { enabled });
      setTestData({ ...testData, test_mode_enabled: enabled });
      toast.success(enabled ? "Тестовый режим включен" : "Тестовый режим выключен");
      fetchTerminals(pagination.page);
    } catch (err) {
      toast.error("Ошибка переключения режима");
    }
  };

  // Export function
  const handleExport = async () => {
    try {
      const response = await api.get('/export/terminals', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `terminals_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Экспорт терминалов завершён');
    } catch (err) {
      toast.error('Ошибка экспорта');
    }
  };

  const formatCurrency = (code) => {
    const curr = CURRENCIES.find(c => c.code === code);
    return curr ? `${curr.code} (${curr.symbol})` : code;
  };

  return (
    <div className="space-y-6" data-testid="terminals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('terminals.title')}</h1>
          <p className="text-slate-400 mt-1">
            {t("common.total")}: {pagination.total} {t("terminals.title").toLowerCase()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleExport}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
            data-testid="export-terminals-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Экспорт
          </Button>
          {canWrite && (
            <Button
              onClick={openCreateDialog}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="create-terminal-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              {t('common.create')}
            </Button>
          )}
          <Button
            onClick={() => fetchTerminals(pagination.page)}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder={t("terminals.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-slate-700/50 border-slate-600 text-white"
              data-testid="terminal-search-input"
            />
          </div>
          <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600">
            {t("terminals.find")}
          </Button>
        </form>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <Select value={filterProvider} onValueChange={setFilterProvider}>
            <SelectTrigger className="w-[180px] bg-slate-700/50 border-slate-600 text-white">
              <SelectValue placeholder={t("terminals.allProviders")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("terminals.allProviders")}</SelectItem>
              {providers.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Terminals Table */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : terminals.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <Terminal className="w-12 h-12 mb-4 opacity-50" />
              <p>Терминалы не найдены</p>
              {canWrite && (
                <Button onClick={openCreateDialog} className="mt-4 bg-emerald-500 hover:bg-emerald-600">
                  <Plus className="w-4 h-4 mr-1" />
                  Добавить первый терминал
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t("terminals.name")}</th>
                    <th className="text-left p-4 text-sm font-medium text-slate-400">{t('terminals.merchant')}</th>
                    <th className="text-center p-4 text-sm font-medium text-slate-400">Валюта</th>
                    <th className="text-center p-4 text-sm font-medium text-slate-400">Комиссия</th>
                    <th className="text-center p-4 text-sm font-medium text-slate-400">Режим</th>
                    <th className="text-center p-4 text-sm font-medium text-slate-400">{t('common.status')}</th>
                    <th className="text-right p-4 text-sm font-medium text-slate-400">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {terminals.map((terminal) => {
                    const settings = terminal.settings || {};
                    const commissions = settings.commissions || terminal.commissions || {};
                    const currency = settings.currency || terminal.currency || 'KZT';
                    
                    return (
                      <tr
                        key={terminal.id}
                        className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors"
                        data-testid={`terminal-row-${terminal.id}`}
                      >
                        <td className="p-4">
                          <div>
                            <span className="text-white font-medium">{terminal.name}</span>
                            <p className="text-xs text-slate-500">ID: {terminal.id}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-slate-300 text-sm">
                            {terminal.merchant_name || '— Все мерчанты —'}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-400">
                            {currency}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          {commissions.percent ? (
                            <div>
                              <span className="text-white font-medium">{commissions.percent}%</span>
                              {commissions.min && (
                                <p className="text-xs text-slate-500">мин. {commissions.min}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {terminal.test_mode ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                              ТЕСТ
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400">
                              ПРОД
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          {terminal.is_active ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-400">
                              <Check className="w-3 h-3" />
                              {t('common.active')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-500/20 text-slate-400">
                              <X className="w-3 h-3" />
                              {t('common.inactive')}
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openRoutingDialog(terminal)}
                              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                              title="Настройка роутинга"
                              data-testid={`routing-terminal-${terminal.id}`}
                            >
                              <Settings className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openTestDialog(terminal)}
                              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                              title="Тестовые данные"
                              data-testid={`test-terminal-${terminal.id}`}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openKeysDialog(terminal)}
                              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/20"
                              data-testid={`keys-terminal-${terminal.id}`}
                              title="API ключи"
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            {canWrite && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEditDialog(terminal)}
                                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                  data-testid={`edit-terminal-${terminal.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openDeleteDialog(terminal)}
                                  className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                                  data-testid={`delete-terminal-${terminal.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {Math.ceil(pagination.total / pagination.pageSize) > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                Страница {pagination.page} из {Math.ceil(pagination.total / pagination.pageSize)}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchTerminals(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchTerminals(pagination.page + 1)}
                  disabled={pagination.page >= Math.ceil(pagination.total / pagination.pageSize)}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog with Tabs */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Terminal className="w-5 h-5 text-emerald-500" />
              {editingTerminal ? 'Редактировать терминал' : 'Новый терминал'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-6 mt-4">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-4 bg-slate-700/50">
                <TabsTrigger value="basic" className="data-[state=active]:bg-emerald-500">Основное</TabsTrigger>
                <TabsTrigger value="commission" className="data-[state=active]:bg-emerald-500">Комиссия</TabsTrigger>
                <TabsTrigger value="limits" className="data-[state=active]:bg-emerald-500">Лимиты</TabsTrigger>
                <TabsTrigger value="gateway" className="data-[state=active]:bg-emerald-500">Провайдер</TabsTrigger>
              </TabsList>
              
              {/* Basic Info Tab */}
              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Название терминала *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Например: FlyOne Terminal 1"
                      className="bg-slate-700/50 border-slate-600 text-white"
                      required
                      data-testid="terminal-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Мерчант</Label>
                    <Select
                      value={formData.merchant_id}
                      onValueChange={(value) => setFormData({ ...formData, merchant_id: value })}
                    >
                      <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                        <SelectValue placeholder="Для всех мерчантов" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Для всех мерчантов</SelectItem>
                        {merchants.map((m) => (
                          <SelectItem key={m.id} value={m.id.toString()}>
                            #{m.id} - {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Валюта терминала</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  >
                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                      <SelectValue placeholder="Выберите валюту" />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.code} — {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                  <Label className="text-slate-300">Терминал активен</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                </div>
              </TabsContent>

              {/* Commission Tab */}
              <TabsContent value="commission" className="space-y-4 mt-4">
                <div className="p-4 bg-slate-700/30 rounded-lg space-y-4">
                  <div className="flex items-center gap-2 text-emerald-400">
                    <Percent className="w-5 h-5" />
                    <h3 className="font-medium">Комиссия эквайринга</h3>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Процент (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.commission_percent}
                        onChange={(e) => setFormData({ ...formData, commission_percent: e.target.value })}
                        placeholder="3.5"
                        className="bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Мин. комиссия</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.commission_min}
                        onChange={(e) => setFormData({ ...formData, commission_min: e.target.value })}
                        placeholder="100"
                        className="bg-slate-700/50 border-slate-600 text-white"
                      />
                      <p className="text-xs text-slate-500">Не менее указанной суммы</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Макс. комиссия</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.commission_max}
                        onChange={(e) => setFormData({ ...formData, commission_max: e.target.value })}
                        placeholder="10000"
                        className="bg-slate-700/50 border-slate-600 text-white"
                      />
                      <p className="text-xs text-slate-500">Не более указанной суммы</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-slate-700/30 rounded-lg space-y-4">
                  <h3 className="font-medium text-amber-400">Дополнительные комиссии</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Комиссия за возврат (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.refund_commission}
                        onChange={(e) => setFormData({ ...formData, refund_commission: e.target.value })}
                        placeholder="0"
                        className="bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Комиссия за chargeback (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.chargeback_commission}
                        onChange={(e) => setFormData({ ...formData, chargeback_commission: e.target.value })}
                        placeholder="0"
                        className="bg-slate-700/50 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Limits Tab */}
              <TabsContent value="limits" className="space-y-4 mt-4">
                <div className="p-4 bg-slate-700/30 rounded-lg space-y-4">
                  <div className="flex items-center gap-2 text-blue-400">
                    <DollarSign className="w-5 h-5" />
                    <h3 className="font-medium">Лимиты платежей</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Минимальная сумма платежа</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.payment_min}
                        onChange={(e) => setFormData({ ...formData, payment_min: e.target.value })}
                        placeholder="100"
                        className="bg-slate-700/50 border-slate-600 text-white"
                      />
                      <p className="text-xs text-slate-500">В валюте терминала ({formData.currency})</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Максимальная сумма платежа</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.payment_max}
                        onChange={(e) => setFormData({ ...formData, payment_max: e.target.value })}
                        placeholder="1000000"
                        className="bg-slate-700/50 border-slate-600 text-white"
                      />
                      <p className="text-xs text-slate-500">В валюте терминала ({formData.currency})</p>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm text-blue-400">
                    <strong>Пример:</strong> При комиссии 3% с минимумом 100 {formData.currency}:
                    <br />• Платеж 2000 {formData.currency} → комиссия 100 {formData.currency} (мин.)
                    <br />• Платеж 5000 {formData.currency} → комиссия 150 {formData.currency} (3%)
                  </p>
                </div>
              </TabsContent>

              {/* Gateway Credentials Tab */}
              <TabsContent value="gateway" className="space-y-4 mt-4">
                <div className="p-4 bg-slate-700/30 rounded-lg space-y-4">
                  <div className="flex items-center gap-2 text-purple-400">
                    <Settings className="w-5 h-5" />
                    <h3 className="font-medium">Настройки провайдера</h3>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-300">Провайдер платежей</Label>
                    <Select 
                      value={formData.gateway_provider || "mrc"} 
                      onValueChange={(v) => setFormData({ ...formData, gateway_provider: v })}
                    >
                      <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                        <SelectValue placeholder="Выберите провайдера" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mrc">МРЦ (СБП)</SelectItem>
                        <SelectItem value="mts">МТС Банк</SelectItem>
                        <SelectItem value="finzo">Finzo (EUR/USD)</SelectItem>
                        <SelectItem value="kapitalbank">Kapitalbank UZ</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* MRC Credentials */}
                {(formData.gateway_provider === "mrc" || !formData.gateway_provider) && (
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-purple-300">МРЦ — Credentials СБП</h3>
                      <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-300 rounded">API v1</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Merchant ID *</Label>
                        <Input
                          type="text"
                          value={formData.gw_merchant_id || ""}
                          onChange={(e) => setFormData({ ...formData, gw_merchant_id: e.target.value })}
                          placeholder="MA0004205661"
                          className="bg-slate-700/50 border-slate-600 text-white font-mono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Ext Entity ID *</Label>
                        <Input
                          type="text"
                          value={formData.gw_ext_entity_id || ""}
                          onChange={(e) => setFormData({ ...formData, gw_ext_entity_id: e.target.value })}
                          placeholder="LB0000150583"
                          className="bg-slate-700/50 border-slate-600 text-white font-mono"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Login *</Label>
                        <Input
                          type="text"
                          value={formData.gw_login || ""}
                          onChange={(e) => setFormData({ ...formData, gw_login: e.target.value })}
                          placeholder="oxypay-bpa"
                          className="bg-slate-700/50 border-slate-600 text-white font-mono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Password *</Label>
                        <Input
                          type="password"
                          value={formData.gw_password || ""}
                          onChange={(e) => setFormData({ ...formData, gw_password: e.target.value })}
                          placeholder="••••••••"
                          className="bg-slate-700/50 border-slate-600 text-white"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Client ID *</Label>
                        <Input
                          type="text"
                          value={formData.gw_client_id || ""}
                          onChange={(e) => setFormData({ ...formData, gw_client_id: e.target.value })}
                          placeholder="oxypay-bpa-api"
                          className="bg-slate-700/50 border-slate-600 text-white font-mono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Account *</Label>
                        <Input
                          type="text"
                          value={formData.gw_account || ""}
                          onChange={(e) => setFormData({ ...formData, gw_account: e.target.value })}
                          placeholder="30232810500009968212"
                          className="bg-slate-700/50 border-slate-600 text-white font-mono"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300">Secret *</Label>
                      <Input
                        type="password"
                        value={formData.gw_secret || ""}
                        onChange={(e) => setFormData({ ...formData, gw_secret: e.target.value })}
                        placeholder="••••••••••••••••"
                        className="bg-slate-700/50 border-slate-600 text-white"
                      />
                      <p className="text-xs text-slate-500">Секретный ключ для подписи запросов</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Payment Type</Label>
                        <Select 
                          value={formData.gw_payment_type || "redirect"} 
                          onValueChange={(v) => setFormData({ ...formData, gw_payment_type: v })}
                        >
                          <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="redirect">Redirect</SelectItem>
                            <SelectItem value="qr">QR Code</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">QRC Type</Label>
                        <Select 
                          value={formData.gw_qrc_type || "02"} 
                          onValueChange={(v) => setFormData({ ...formData, gw_qrc_type: v })}
                        >
                          <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="01">01 — Одноразовый</SelectItem>
                            <SelectItem value="02">02 — Многоразовый</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                      <p className="text-sm text-emerald-400">
                        <strong>Callback URL:</strong> https://business.oxypay.kz/webhooks/mrc/{editingTerminal?.id || 'TERMINAL_ID'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Finzo Credentials */}
                {formData.gateway_provider === "finzo" && (
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-blue-300">Finzo — Credentials</h3>
                      <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-300 rounded">API v1</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Public Key *</Label>
                        <Input
                          type="text"
                          value={formData.gw_finzo_public_key || ""}
                          onChange={(e) => setFormData({ ...formData, gw_finzo_public_key: e.target.value })}
                          placeholder="pk_live_..."
                          className="bg-slate-700/50 border-slate-600 text-white font-mono"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300">Secret Key *</Label>
                        <Input
                          type="password"
                          value={formData.gw_finzo_secret_key || ""}
                          onChange={(e) => setFormData({ ...formData, gw_finzo_secret_key: e.target.value })}
                          placeholder="sk_live_..."
                          className="bg-slate-700/50 border-slate-600 text-white"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300">Режим</Label>
                      <div className="flex gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setFormData({ ...formData, gw_finzo_test_mode: true })}
                          className={formData.gw_finzo_test_mode ? "bg-amber-500/30 border-amber-500 text-amber-300" : "border-slate-600 text-slate-400"}
                        >
                          Sandbox
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setFormData({ ...formData, gw_finzo_test_mode: false })}
                          className={!formData.gw_finzo_test_mode ? "bg-emerald-500/30 border-emerald-500 text-emerald-300" : "border-slate-600 text-slate-400"}
                        >
                          Production
                        </Button>
                      </div>
                    </div>

                    <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg space-y-2">
                      <p className="text-sm text-blue-400">
                        <strong>Callback URL:</strong> https://business.oxypay.kz/callbacks/finzo
                      </p>
                      <p className="text-xs text-blue-400/70">
                        Валюты: EUR, USD | Карты: Visa, Mastercard
                      </p>
                    </div>

                    <div className="text-xs text-slate-500">
                      Получить ключи: <a href="https://dashboard.finzo.biz" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">dashboard.finzo.biz</a> → Developers → API Keys
                    </div>
                  </div>
                )}

                {/* Test Connection Button */}
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-purple-500 text-purple-300 hover:bg-purple-500/20"
                    onClick={() => {
                      toast.success("Подключение к провайдеру настроено");
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Проверить подключение
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6 pt-4 border-t border-slate-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="border-slate-600 text-slate-300"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                disabled={saving}
              >
                {saving && <RefreshCw className="w-4 h-4 animate-spin mr-1" />}
                {editingTerminal ? 'Сохранить' : 'Создать'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Удалить терминал?
            </DialogTitle>
          </DialogHeader>
          <p className="text-slate-300 mt-2">
            Вы уверены, что хотите удалить терминал <strong>{deletingTerminal?.name}</strong>?
          </p>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setDeleteDialog(false)} className="border-slate-600 text-slate-300">
              {t('common.cancel')}
            </Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Keys Dialog */}
      <Dialog open={keysDialog} onOpenChange={setKeysDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-500" />
              API ключи: {selectedTerminalKeys?.name}
            </DialogTitle>
          </DialogHeader>

          {selectedTerminalKeys && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <label className="text-sm text-slate-400">API Key (Terminal)</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-slate-900 rounded-lg font-mono text-sm text-emerald-400 overflow-x-auto break-all">
                    {showSecretKey ? (selectedTerminalKeys.api_key || 'Не сгенерирован') : '••••••••••••••••••••••••••••••••••••••••'}
                  </code>
                  <Button variant="outline" size="sm" onClick={() => setShowSecretKey(!showSecretKey)} className="border-slate-600 text-slate-300">
                    {showSecretKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  {selectedTerminalKeys.api_key && (
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(selectedTerminalKeys.api_key, 'API Key')} className="border-slate-600 text-slate-300">
                      <Copy className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              {selectedTerminalKeys.merchant_name && (
                <div className="p-3 bg-slate-700/30 rounded-lg">
                  <p className="text-xs text-slate-500">Мерчант</p>
                  <p className="text-white font-medium">{selectedTerminalKeys.merchant_name}</p>
                </div>
              )}
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <p className="text-sm text-emerald-400">
                  <strong>Документация:</strong> <a href="https://docs.oxypay.kz" target="_blank" rel="noopener noreferrer" className="underline">docs.oxypay.kz</a>
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Routing Dialog */}
      <Dialog open={showRoutingDialog} onOpenChange={setShowRoutingDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-400" />
              Настройка роутинга платежей
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Терминал: {routingTerminal?.name} (ID: {routingTerminal?.id})
            </DialogDescription>
          </DialogHeader>

          {loadingRouting ? (
            <div className="flex justify-center py-8"><RefreshCw className="w-8 h-8 animate-spin text-purple-400" /></div>
          ) : routingData && (
            <div className="space-y-6 mt-4">
              <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                <div>
                  <p className="font-medium text-white">Роутинг включён</p>
                  <p className="text-sm text-slate-400">Автоматический выбор провайдера по типу карты</p>
                </div>
                <Button
                  variant={routingData.enabled ? "default" : "outline"}
                  onClick={() => setRoutingData({...routingData, enabled: !routingData.enabled})}
                  className={routingData.enabled ? "bg-emerald-500 hover:bg-emerald-600" : "border-slate-600"}
                >
                  {routingData.enabled ? "Включён" : "Выключен"}
                </Button>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Метод оплаты по умолчанию</Label>
                <div className="flex gap-2">
                  {PAYMENT_METHODS.map(pm => (
                    <Button
                      key={pm.id} type="button" variant="outline"
                      onClick={() => setRoutingData({...routingData, default_payment_method: pm.id})}
                      className={routingData.default_payment_method === pm.id ? "bg-emerald-500/30 border-emerald-500 text-emerald-300" : "border-slate-600 text-slate-400"}
                    >
                      {pm.name}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Провайдер по умолчанию</Label>
                <Select value={routingData.default_provider || ""} onValueChange={(v) => setRoutingData({...routingData, default_provider: v})}>
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                    <SelectValue placeholder="Выберите провайдера" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROVIDERS
                      .filter(p => p.supported_methods?.includes(routingData.default_payment_method || "card"))
                      .map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">Правила роутинга</Label>
                  <Button variant="outline" size="sm" onClick={addRoutingRule} className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20">
                    <Plus className="w-4 h-4 mr-1" /> Добавить правило
                  </Button>
                </div>
                {routingData.routing_rules?.length === 0 ? (
                  <p className="text-slate-500 text-sm py-4 text-center">Нет правил. Будет использоваться провайдер по умолчанию.</p>
                ) : (
                  <div className="space-y-3">
                    {routingData.routing_rules?.map((rule, idx) => (
                      <div key={rule.id} className="p-4 bg-slate-700/30 rounded-lg border border-slate-600 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-300">Правило #{idx + 1}</span>
                          <Button variant="ghost" size="sm" onClick={() => deleteRule(rule.id)} className="text-red-400 hover:bg-red-500/20">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        {/* Payment Method Selection */}
                        <div>
                          <Label className="text-xs text-slate-400">Метод оплаты</Label>
                          <div className="flex gap-2 mt-1">
                            {PAYMENT_METHODS.map(pm => (
                              <Button
                                key={pm.id} type="button" variant="outline" size="sm"
                                onClick={() => {
                                  updateRule(rule.id, "payment_method", pm.id);
                                  // Clear card types if switching to SBP
                                  if (pm.id === "sbp") {
                                    updateRule(rule.id, "card_types", []);
                                  }
                                }}
                                className={rule.payment_method === pm.id ? "bg-emerald-500/30 border-emerald-500 text-emerald-300" : "border-slate-600 text-slate-400"}
                              >
                                {pm.name}
                              </Button>
                            ))}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          {/* Card Types - only show for card payment method */}
                          {(rule.payment_method === "card" || !rule.payment_method) && (
                            <div>
                              <Label className="text-xs text-slate-400">Типы карт</Label>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {["visa", "mastercard", "mir", "uzcard", "humo"].map(ct => (
                                  <Button
                                    key={ct} type="button" variant="outline" size="sm"
                                    onClick={() => {
                                      const types = rule.card_types || [];
                                      updateRule(rule.id, "card_types", types.includes(ct) ? types.filter(t => t !== ct) : [...types, ct]);
                                    }}
                                    className={rule.card_types?.includes(ct) ? "bg-purple-500/30 border-purple-500 text-purple-300" : "border-slate-600 text-slate-400"}
                                  >
                                    {ct.toUpperCase()}
                                  </Button>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* SBP info when SBP selected */}
                          {rule.payment_method === "sbp" && (
                            <div>
                              <Label className="text-xs text-slate-400">Метод</Label>
                              <div className="mt-1 p-2 bg-blue-500/20 border border-blue-500/30 rounded text-sm text-blue-300">
                                СБП — Система Быстрых Платежей
                              </div>
                            </div>
                          )}
                          
                          <div>
                            <Label className="text-xs text-slate-400">Провайдер</Label>
                            <Select value={rule.provider || ""} onValueChange={(v) => updateRule(rule.id, "provider", v)}>
                              <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white mt-1">
                                <SelectValue placeholder="Выберите" />
                              </SelectTrigger>
                              <SelectContent>
                                {PROVIDERS
                                  .filter(p => p.supported_methods?.includes(rule.payment_method || "card"))
                                  .map(p => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Input placeholder="Описание правила" value={rule.description || ""} onChange={(e) => updateRule(rule.id, "description", e.target.value)} className="bg-slate-700/50 border-slate-600 text-white text-sm" />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-4 bg-slate-700/30 rounded-lg border border-dashed border-slate-600">
                <Label className="text-slate-300">Проверить карту</Label>
                <div className="flex gap-2 mt-2">
                  <Input placeholder="Номер карты (мин. 6 цифр)" value={testCard} onChange={(e) => setTestCard(e.target.value.replace(/\D/g, ""))} className="bg-slate-700/50 border-slate-600 text-white font-mono" maxLength={19} />
                  <Button type="button" onClick={testCardRouting} disabled={testCard.length < 6} className="bg-purple-500 hover:bg-purple-600">Проверить</Button>
                </div>
                {testResult && (
                  <div className="mt-3 p-3 bg-slate-800 rounded text-sm">
                    <p className="text-slate-300">Тип: <span className="text-purple-400 font-medium">{testResult.card_type?.toUpperCase()}</span></p>
                    <p className="text-slate-300">Провайдер: <span className="text-emerald-400 font-medium">{testResult.provider_details?.name || testResult.provider}</span></p>
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setShowRoutingDialog(false)} className="border-slate-600 text-slate-300">Отмена</Button>
                <Button type="button" onClick={saveRouting} disabled={savingRouting} className="bg-purple-500 hover:bg-purple-600">
                  {savingRouting && <RefreshCw className="w-4 h-4 animate-spin mr-2" />}
                  Сохранить
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Test Data Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-400" />
              Тестовые данные для интеграции
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Терминал: {testTerminal?.name} (ID: {testTerminal?.id})
            </DialogDescription>
          </DialogHeader>

          {loadingTest ? (
            <div className="flex justify-center py-8"><RefreshCw className="w-8 h-8 animate-spin text-amber-400" /></div>
          ) : testData && (
            <div className="space-y-6 mt-4">
              <div className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg">
                <div>
                  <p className="font-medium text-white">Тестовый режим</p>
                  <p className="text-sm text-slate-400">Включите для использования тестовых карт</p>
                </div>
                <Switch checked={testData.test_mode_enabled} onCheckedChange={toggleTestMode} />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">API Key</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-slate-900 rounded-lg font-mono text-sm text-emerald-400 overflow-x-auto">
                    {testData.api_key || 'Не сгенерирован'}
                  </code>
                  {testData.api_key && (
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(testData.api_key, 'API Key')} className="border-slate-600 text-slate-300">
                      <Copy className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                  Тестовые карты
                </h3>
                {Object.entries(testData.test_cards || {}).map(([cardType, cards]) => (
                  <div key={cardType} className="space-y-2">
                    <h4 className="text-sm font-medium text-slate-300 uppercase">{cardType}</h4>
                    <div className="grid gap-2">
                      {cards.map((card, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                          <div>
                            <code className="text-white font-mono">{card.number}</code>
                            <p className="text-xs text-slate-400 mt-1">Срок: {card.expiry} | CVV: {card.cvv}</p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            card.result === 'success' ? 'bg-emerald-500/20 text-emerald-400' :
                            card.result === 'decline' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {card.result === 'success' ? 'УСПЕХ' : card.result === 'decline' ? 'ОТКАЗ' : '3DS'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TerminalsPage;
